import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DateRange } from "react-date-range";
import format from "date-fns/format";
import CalendarIcon from "../../../assets/svg/Calendar";

const DATE_FORMAT = "dd-MMM-yyyy";

function DateRangePicker({
  label,
  name,
  disabled,
  startDateVal,
  endDateVal,
  maxDate,
  maxDays,
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [selectedDate, setSelectedDate] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  const containerRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState("left");

  const toggle = () => setOpen((prev) => !prev);

  const handleSelection = (item) => {
    const { startDate } = item.selection;
    let { endDate } = item.selection;

    if (maxDays && startDate && endDate) {
      const maxAllowedDate = new Date(startDate);
      maxAllowedDate.setDate(maxAllowedDate.getDate() + maxDays);

      if (endDate > maxAllowedDate) {
        endDate = maxAllowedDate;
      }
    }

    setSelectedDate([{ startDate, endDate, key: "selection" }]);
  };

  const formatStartDate = selectedDate[0]?.startDate
    ? format(selectedDate[0].startDate, DATE_FORMAT)
    : "";
  const formatEndDate = selectedDate[0]?.endDate
    ? format(selectedDate[0].endDate, DATE_FORMAT)
    : "";

  const displayValue =
    formatStartDate && formatEndDate
      ? `${formatStartDate} - ${formatEndDate}`
      : "";

  useEffect(() => {
    setSelectedDate([
      {
        key: "selection",
        startDate: startDateVal ? new Date(startDateVal) : null,
        endDate: endDateVal ? new Date(endDateVal) : null,
      },
    ]);
  }, [startDateVal, endDateVal]);

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const popupWidth = 400;

      if (viewportWidth - rect.left < popupWidth) {
        setPopupPosition("right");
      } else {
        setPopupPosition("left");
      }
    }
  }, [open]);

  return (
    <Box sx={{ position: "relative" }} ref={containerRef}>
      <TextField
        fullWidth
        label={label}
        variant="outlined"
        value={displayValue}
        onClick={!disabled ? toggle : undefined}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarIcon />
            </InputAdornment>
          ),
        }}
      />

      {open && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              mt: "4px",
              zIndex: 9999,
              left: popupPosition === "left" ? 0 : "auto",
              right: popupPosition === "right" ? 0 : "auto",
              boxShadow: 3,
              backgroundColor: "#fff",
              borderRadius: 1,
            }}
          >
            <DateRange
              editableDateInputs
              onChange={handleSelection}
              moveRangeOnFirstSelection={false}
              ranges={selectedDate}
              months={isMobile ? 1 : 2}
              direction={isMobile ? "vertical" : "horizontal"}
              showDateDisplay={false}
              maxDate={maxDate}
            />
          </Box>
          {/* Close calendar when clicked outside */}
          <Box
            onClick={toggle}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 999,
            }}
          />
        </>
      )}

      {/* Hidden inputs for form submission */}
      <input type="hidden" name={`${name}From`} value={formatStartDate} />
      <input type="hidden" name={`${name}To`} value={formatEndDate} />
    </Box>
  );
}

DateRangePicker.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  startDateVal: PropTypes.string,
  endDateVal: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  maxDays: PropTypes.number,
};

DateRangePicker.defaultProps = {
  disabled: false,
  startDateVal: "",
  endDateVal: "",
  maxDate: undefined,
  maxDays: undefined,
};

export default DateRangePicker;
